<script setup lang="tsx">
import { useI18n } from 'vue-i18n'
import PlusButton from '@/components/widgets/PlusButton.vue'
import { formatPercentage, formatPrice, statusColumns, defaultMitigationSummary, defaultMitigationMeasures } from '../shared'
import MitigationMeasureRow from './MitigationMeasureRow.vue'
import IconButton from '@/components/shared/IconButton.vue'
import MeasureSelect from '../MeasureSelect.vue'
import { computed, ref, watch } from 'vue'
import type { MitigationMeasureSummary, StandardMethod, StandardModule, Response } from '@/models/api-generated'
import http from '@/services/http'
import { TagModule } from '@/models/enums-generated'
import Autocomplete from '@/components/widgets/Autocomplete.vue'

const props = defineProps<{
	isEdit: boolean
	riskMetaId: number
	instanceId: number
	assetId: number
}>()

const model = defineModel<MitigationMeasureSummary[]>({ required: true })
const { t } = useI18n()

const selectedModule = ref<StandardModule>()

function getGroup(moduleName: string) {
	const index = model.value.findIndex((g) => g.moduleName === moduleName)
	return index >= 0 ? { group: model.value[index], index } : null
}

const deletedIds = new Map<string, Set<number>>()
function getDeletedSet(moduleName: string) {
	if (!deletedIds.has(moduleName)) deletedIds.set(moduleName, new Set())
	return deletedIds.get(moduleName)!
}

function getGroupMeta(group: MitigationMeasureSummary) {
	const deleted = getDeletedSet(group.moduleName)
	let hasCustom = false
	let allCustom = true
	let allDeleted = true
	for (const m of group.mitigationMeasures) {
		const c = m.isCustom
		const d = deleted.has(m.id)
		if (c) hasCustom = true
		if (!c) allCustom = false
		if (!d) allDeleted = false
	}
	return { hasCustom, allCustom, allDeleted }
}

function deleteGroup(group: MitigationMeasureSummary) {
	const found = getGroup(group.moduleName)
	if (!found) return
	const { index } = found
	const original = model.value[index]
	const set = getDeletedSet(group.moduleName)
	for (const m of original.mitigationMeasures) if (m.id > 0) set.add(m.id)
	const remaining = original.mitigationMeasures.filter((m) => m.id > 0)
	if (remaining.length === 0) {
		model.value.splice(index, 1)
		return
	}
	model.value[index] = {
		...original,
		mitigationMeasures: remaining,
		deletedIds: Array.from(set),
	}
}

function toggleDeleted(moduleName: string, id: number, index: number) {
	const found = getGroup(moduleName)
	if (!found) return

	const { group, index: gIndex } = found

	const measure = group.mitigationMeasures[index]
	if (measure.id === 0) {
		group.mitigationMeasures.splice(index, 1)

		if (group.mitigationMeasures.length === 0) {
			model.value.splice(gIndex, 1)
		}
		return
	}

	const set = getDeletedSet(moduleName)

	if (set.has(id)) set.delete(id)
	else set.add(id)

	model.value[gIndex] = {
		...group,
		deletedIds: Array.from(set),
	}
}

const groupMethods = (group: MitigationMeasureSummary) =>
	computed<StandardMethod[]>({
		get() {
			const deleted = getDeletedSet(group.moduleName)
			return group.mitigationMeasures.filter((m) => m.isCustom && !deleted.has(m.id)).map((m) => m.method)
		},
		set(methods) {
			if (!methods.length) return
			const deleted = getDeletedSet(group.moduleName)
			const selectedIds = new Set(methods.map((m) => m.id))
			const kept = []
			const existing = new Set<number>()
			for (const m of group.mitigationMeasures) {
				const sel = selectedIds.has(m.methodId)
				if (m.isCustom) {
					existing.add(m.methodId)
					if (m.id > 0) {
						if (sel) deleted.delete(m.id)
						else deleted.add(m.id)
					}
					if (m.id > 0 || sel) kept.push(m)
				} else {
					kept.push(m)
				}
			}
			const newOnes = methods.filter((m) => !existing.has(m.id))
			group.mitigationMeasures = [
				...kept,
				...defaultMitigationMeasures(group.moduleId, group.moduleName, newOnes, props.instanceId, props.riskMetaId),
			]
			group.deletedIds = Array.from(deleted)
		},
	})

function canEditGroup(group: MitigationMeasureSummary) {
	const m = getGroupMeta(group)
	return m.hasCustom || group.mitigationMeasures.length === 0
}

const grouped = computed(() => model.value.filter((g) => g.moduleName !== 'total'))

const usedModuleIds = computed(() => grouped.value.map((g) => g.moduleId))

const totalSummary = computed(() => model.value.find((g) => g.moduleName === 'total') || defaultMitigationSummary())

async function fetchModuleMeasures(moduleId: number) {
	const { data } = await http.get<Response<StandardMethod[]>>('api/risk/measures', { params: { moduleId } })
	return data.data ?? []
}

watch(selectedModule, async (mod) => {
	if (!mod) return
	const measures = await fetchModuleMeasures(mod.id)
	const summary = defaultMitigationSummary(props.instanceId, props.riskMetaId, mod, measures)
	model.value.push(summary)
	selectedModule.value = undefined
})

function goToMethodsPage(modules: MitigationMeasureSummary[]) {
	const params = new URLSearchParams()
	for (const m of modules) params.append('module', m.moduleName)
	window.location.href = `/app/methods?${params.toString()}`
}
</script>

<template>
	<div class="card">
		<div class="card-body">
			<h6 class="card-title text-center fw-bold">{{ t('risk.mitigationMeasure', 2) }}</h6>

			<div class="table-responsive w-100">
				<table class="mitigation-measures-table">
					<thead>
						<tr>
							<th class="w-auto-32"></th>
							<th>{{ t('module.label') }}/{{ t('method.label') }}</th>
							<th></th>
							<th></th>
							<th v-for="s in statusColumns" :key="s.key">
								<i :class="['bi bi-square-fill', s.color]" v-one-tooltip="s.label"></i>
							</th>
							<th><i class="bi bi-link-45deg" v-one-tooltip="t('risk.assetAssociation')"></i></th>
							<th><i class="bi bi-currency-exchange" v-one-tooltip="t('risk.price')"></i></th>
							<th><i class="bi bi-sort-up" v-one-tooltip="t('risk.priority')"></i></th>
							<th><i class="bi bi-clock" v-one-tooltip="t('risk.dueDate')"></i></th>
							<th><i class="bi bi-person-circle" v-one-tooltip="t('risk.responsible')"></i></th>
						</tr>
					</thead>

					<tbody>
						<tr class="summary-row">
							<td class="w-auto-32"></td>
							<td>
								<span @click="goToMethodsPage(model)" class="risks-cell">
									{{ t('risk.summary') }}
								</span>
							</td>
							<td>{{ totalSummary.applicableCount }} / {{ totalSummary.mitigationMeasures.length }}</td>
							<td></td>
							<td>{{ formatPercentage(totalSummary.moduleStatuses.i.percentage + totalSummary.moduleStatuses.ci.percentage) }}</td>
							<td>{{ formatPercentage(totalSummary.moduleStatuses.pi.percentage) }}</td>
							<td>{{ formatPercentage(totalSummary.moduleStatuses.ni.percentage) }}</td>
							<td>{{ formatPercentage(totalSummary.moduleStatuses.wna.percentage + totalSummary.moduleStatuses.wni.percentage) }}</td>
							<td colspan="2">{{ formatPercentage(totalSummary.moduleStatuses.na.percentage) }}</td>
							<td colspan="4">{{ formatPrice(totalSummary.price) }}</td>
							<td></td>
						</tr>
					</tbody>

					<tbody>
						<template v-for="group in grouped" :key="group.moduleName">
							<tr
								class="group-row"
								:class="{
									'table-danger': getGroupMeta(group).allDeleted,
									'table-info': group.mitigationMeasures.some((m) => m.isCustom && m.id === 0),
								}"
							>
								<td class="text-center">
									<PlusButton v-model="group.isExpanded" />
								</td>
								<td>{{ group.moduleName }}</td>
								<td>{{ group.applicableCount }} / {{ group.mitigationMeasures.length }}</td>
								<td>
									<MeasureSelect
										v-if="isEdit && canEditGroup(group)"
										:module-id="group.moduleId"
										v-model="groupMethods(group).value"
										:valid-input="groupMethods(group).value.length > 0"
									/>
								</td>
								<td>{{ formatPercentage(group.moduleStatuses.i.percentage + group.moduleStatuses.ci.percentage) }}</td>
								<td>{{ formatPercentage(group.moduleStatuses.pi.percentage) }}</td>
								<td>{{ formatPercentage(group.moduleStatuses.ni.percentage) }}</td>
								<td>{{ formatPercentage(group.moduleStatuses.wna.percentage + group.moduleStatuses.wni.percentage) }}</td>
								<td colspan="2">{{ formatPercentage(group.moduleStatuses.na.percentage) }}</td>
								<td colspan="4">{{ formatPrice(group.price) }}</td>
								<td>
									<IconButton
										v-if="getGroupMeta(group).allCustom && isEdit"
										data-test="module-delete-button"
										class="bi bi-trash text-danger"
										@click="deleteGroup(group)"
										:title="$t('misc.cancel')"
										size="fs-5"
									/>
								</td>
							</tr>

							<template v-if="group.isExpanded">
								<template v-for="(measure, i) in group.mitigationMeasures" :key="`${group.moduleId}-${measure.methodId}-${i}`">
									<MitigationMeasureRow
										v-model="group.mitigationMeasures[i]"
										:is-edit="props.isEdit"
										:deleted-ids="getDeletedSet(group.moduleName)"
										:on-delete="(id) => toggleDeleted(group.moduleName, id, i)"
										:asset-id="props.assetId"
									/>
								</template>
							</template>
						</template>
					</tbody>
				</table>
			</div>

			<div v-if="props.isEdit" class="my-3">
				<div class="row align-items-center mb-2">
					<div class="col-4">
						<Autocomplete
							v-model="selectedModule"
							id="module-select"
							:url="`/api/risk/modules`"
							:label="$t('misc.search')"
							:type="TagModule"
							:exclude-ids="usedModuleIds"
							:asset-id="props.assetId"
							:valid-input="!!selectedModule"
						/>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<style lang="sass" scoped>
.mitigation-measures-table
  width: 100%
  border-collapse: separate
  border-spacing: 0 4px
  thead
    font-weight: bold
    color: var(--bs-body-color)
    tr th
      background-color: transparent

th,
td
  padding: .5rem 1rem

.summary-row td
  background-color: var(--bs-secondary-bg)
  font-weight: bold
  border-top: 1px solid var(--bs-border-color)
  border-bottom: 1px solid var(--bs-border-color)

.group-row td
  background-color: var(--bs-body-bg)
  border-top: 1px solid var(--bs-border-color)
  border-bottom: 1px solid var(--bs-border-color)

.group-row td:first-child,
.summary-row td:first-child
  border-top-left-radius: .5rem
  border-bottom-left-radius: .5rem
  border-left: 1px solid var(--bs-border-color)

.group-row td:last-child,
.summary-row td:last-child
  border-top-right-radius: .5rem
  border-bottom-right-radius: .5rem
  border-right: 1px solid var(--bs-border-color)

.group-row.table-info td
  background-color: var(--bs-info-bg-subtle) !important
  color: var(--bs-body-color) !important

.group-row.table-danger td
  background-color: var(--bs-danger-bg-subtle) !important
  color: var(--bs-body-color) !important
</style>
